// /opt/isp_erp/frontend/src/components/tickets/FollowUpNotifications.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Tooltip,
  Collapse,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Today as TodayIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useAuth } from '../../auth/useAuth';
import { getCallCenterTickets, updateCallCenterTicket } from '../../api/callcenter.api';
import axios from '../../api/axios';
import { getToken } from '../../store/auth.store';

export default function FollowUpNotifications({ onTicketUpdate }) {
  const { user, isAdmin, isSupervisor } = useAuth();
  const [notifications, setNotifications] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadNotifications();
    // Recargar cada 2 minutos
    const interval = setInterval(loadNotifications, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get('/call-center/follow-up/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFollowUp = async () => {
    try {
      await updateCallCenterTicket(selectedTicket.id, {
        follow_up_completed: true,
        follow_up_notes: completeNotes || 'Seguimiento completado'
      });
      
      setSnackbar({
        open: true,
        message: `✅ Seguimiento completado para ${selectedTicket.ticket_number}`,
        severity: 'success'
      });
      
      setDialogOpen(false);
      setSelectedTicket(null);
      setCompleteNotes('');
      loadNotifications();
      
      if (onTicketUpdate) onTicketUpdate();
    } catch (error) {
      console.error('Error completando seguimiento:', error);
      setSnackbar({
        open: true,
        message: 'Error al completar el seguimiento',
        severity: 'error'
      });
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getTotalNotifications = () => {
    if (!notifications) return 0;
    const resumen = notifications.resumen || {};
    return (resumen.vencidos || 0) + (resumen.hoy || 0);
  };

  const totalNotifications = getTotalNotifications();
  const hasNotifications = totalNotifications > 0;

  if (!hasNotifications) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="success" icon={<CheckCircleIcon />}>
          ✅ No hay seguimientos pendientes. ¡Todo al día!
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Paper sx={{ p: 2, bgcolor: '#fff8e1' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={totalNotifications} color="error" max={99}>
              <NotificationsIcon color="warning" />
            </Badge>
            <Typography variant="h6">
              📋 Seguimientos Pendientes
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={loadNotifications}
              disabled={loading}
              sx={{ ml: 1 }}
            >
              Actualizar
            </Button>
          </Box>
          <Box>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {notifications?.resumen && (
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#ffebee' }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      ⚠️ Vencidos
                    </Typography>
                    <Typography variant="h5" color="error">
                      {notifications.resumen.vencidos || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#fff3e0' }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      📅 Hoy
                    </Typography>
                    <Typography variant="h5" color="warning">
                      {notifications.resumen.hoy || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#e3f2fd' }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      📆 Mañana
                    </Typography>
                    <Typography variant="h5" color="info">
                      {notifications.resumen.manana || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#e8f5e9' }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      📊 Pendientes
                    </Typography>
                    <Typography variant="h5" color="success">
                      {notifications.resumen.total_pendientes || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tickets VENCIDOS */}
          {notifications?.tickets_vencidos && notifications.tickets_vencidos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                ⚠️ Tickets VENCIDOS
              </Typography>
              <Grid container spacing={1}>
                {notifications.tickets_vencidos.slice(0, 5).map((ticket) => {
                  const diasVencidos = Math.floor((new Date() - new Date(ticket.follow_up_date)) / (1000 * 60 * 60 * 24));
                  return (
                    <Grid item xs={12} sm={6} md={4} key={ticket.id}>
                      <Card sx={{ borderLeft: '4px solid #f44336', bgcolor: '#fff5f5' }}>
                        <CardContent sx={{ py: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {ticket.ticket_number}
                            </Typography>
                            <Chip label={`${diasVencidos} días`} color="error" size="small" />
                          </Box>
                          <Typography variant="body2">{ticket.customer_name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Seguimiento: {new Date(ticket.follow_up_date).toLocaleDateString()}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            sx={{ mt: 1 }}
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setDialogOpen(true);
                            }}
                          >
                            Completar Seguimiento
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {notifications.tickets_vencidos.length > 5 && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  + {notifications.tickets_vencidos.length - 5} más vencidos
                </Typography>
              )}
            </Box>
          )}

          {/* Tickets de HOY */}
          {notifications?.tickets_hoy && notifications.tickets_hoy.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="warning" gutterBottom>
                📅 Tickets con seguimiento HOY
              </Typography>
              <Grid container spacing={1}>
                {notifications.tickets_hoy.slice(0, 5).map((ticket) => (
                  <Grid item xs={12} sm={6} md={4} key={ticket.id}>
                    <Card sx={{ borderLeft: '4px solid #ff9800', bgcolor: '#fff8e1' }}>
                      <CardContent sx={{ py: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="bold">
                            {ticket.ticket_number}
                          </Typography>
                          <Chip label="Hoy" color="warning" size="small" />
                        </Box>
                        <Typography variant="body2">{ticket.customer_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          📞 {ticket.customer_phone}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setDialogOpen(true);
                          }}
                        >
                          Completar Seguimiento
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {notifications.tickets_hoy.length > 5 && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  + {notifications.tickets_hoy.length - 5} más hoy
                </Typography>
              )}
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* Dialog para completar seguimiento */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              ✅ Completar Seguimiento
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Ticket:</strong> {selectedTicket?.ticket_number}
              </Typography>
              <Typography variant="body2">
                <strong>Cliente:</strong> {selectedTicket?.customer_name}
              </Typography>
              <Typography variant="body2">
                <strong>Teléfono:</strong> {selectedTicket?.customer_phone}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha de seguimiento:</strong> {selectedTicket?.follow_up_date && new Date(selectedTicket.follow_up_date).toLocaleDateString()}
              </Typography>
              {selectedTicket?.follow_up_notes && (
                <Typography variant="body2">
                  <strong>Notas:</strong> {selectedTicket.follow_up_notes}
                </Typography>
              )}
            </Alert>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notas del seguimiento (opcional)"
              value={completeNotes}
              onChange={(e) => setCompleteNotes(e.target.value)}
              placeholder="Describe lo que se hizo en el seguimiento..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCompleteFollowUp}
            startIcon={<CheckCircleIcon />}
          >
            Completar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
